import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Buttons, FlexContainer } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { authErrorSelector } from 'Store/modules/app/selectors';
import { signUpSetEmailAction } from 'Store/modules/auth/actions';
import { setAuthErrorAction } from 'Store/modules/app/actions';
import { useTranslation } from 'Hooks/useTranslation';
import EmailInput from 'Components/UI/EmailInput';
import { routes } from 'Constants/routes';

const initialState = {
  value: '',
  isValid: false,
};

const Form = (): JSX.Element => {
  const authError = useSelector(authErrorSelector);
  const { t } = useTranslation(locizeNS.COMMON);
  const dispatch = useDispatch();
  const [email, setEmail] = useState(initialState);

  useEffect(() => {
    return () => {
      dispatch(setAuthErrorAction(null));
    };
  }, []);

  useEffect(() => {
    if (email.isValid) {
      dispatch(setAuthErrorAction(null));
    }
  }, [email]);

  const handleOnChange = ({ value }): void => {
    setEmail(prev => ({ ...prev, ...value }));
  };

  const hasWarning = useMemo(() => authError?.type === 'warning', [authError]);

  const onSubmit = (e): void => {
    e.stopPropagation();
    e.preventDefault();
    if (hasWarning) {
      history.push(routes.SIGNUP_STEP_TWO);
    } else {
      dispatch(signUpSetEmailAction({ email: email.value }));
    }
  };

  const isValidForm = useMemo(
    () =>
      (email.value !== '' && email.isValid) || (email.value !== '' && email.isValid && hasWarning),
    [email]
  );

  return (
    <form onSubmit={onSubmit} data-testid="form">
      <FlexContainer direction="column" gap="dimension_24">
        <EmailInput
          value={email.value}
          onChange={handleOnChange}
          hasError={authError}
          errorMsg={authError?.message}
          hasWarning={hasWarning}
        />
        <Buttons.Classic
          label={t('Button.continue')}
          type="submit"
          testID="signUpSubmit"
          mode="primary"
          size="l"
          fullWidth
          disabled={!isValidForm}
        />
      </FlexContainer>
    </form>
  );
};

export default Form;
